<template>
	<button
		:id="id"
		:name="name"
		@click.prevent="handleClick"
		@click.stop="handleClickStop"
		@click="handleSelected"
		class="btn"
		:class="[
			className,
			disabledInfo ? 'is-disabled-w-info' : '',
			selectable && 'is-selectable',
			isSelected && 'is-selected'
		]"
		:title="title"
		:disabled="disabled"
		:variant="variant"
		:type="type"
	>
		<Spinner
			class="btn-spinner-overlay"
			v-if="isLoading"
			:processor="{ loading: true }"
			:variant="'buttons'"
			data-test="spinner"
		/>
		<i
			v-if="iconBeforeLabel === true"
			class="icon btn-icon-before"
			:class="iconClassName"
		></i>
		<slot>
			<span v-if="labelText" class="btn__label">{{ labelText }}</span>
		</slot>
		<i
			v-if="iconBeforeLabel === false"
			class="icon btn-icon-after"
			:class="iconClassName"
		></i>
	</button>
</template>

<script>
import Spinner from '@/components/visual/spinner/spinner';

export default {
	name: 'Button',
	components: {
		Spinner
	},
	data() {
		return {
			isSelected: false
		};
	},
	props: {
		// only applies for default buttons
		selectable: {
			type: Boolean,
			default: false
		},
		selected: {
			type: Boolean,
			default: false
		},
		className: {
			type: String,
			default: ''
		},
		title: {
			type: String,
			default: ''
		},
		iconClassName: {
			type: String,
			default: ''
		},
		iconBeforeLabel: {
			type: Boolean,
			default: null
		},
		labelText: {
			type: String,
			default: null
		},
		btnClick: {
			type: Function,
			default: () => {}
		},
		btnClickStop: {
			type: Function,
			default: () => {}
		},
		type: {
			type: String,
			default: ''
		},
		disabledInfo: {
			type: Boolean,
			default: false
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	watch: {
		selectable(newValue) {
			if (!newValue) {
				this.isSelected = false;
			}
		},
		selected(newValue) {
			this.selectable = true;
			this.isSelected = newValue;
		}
	},
	methods: {
		handleSelected() {
			if (this.selectable) {
				this.isSelected = !this.isSelected;
				this.$emit('selected', this.isSelected);
			}
		},
		handleClick() {
			if (!this.isLoading) {
				this.btnClick();
				this.$emit('click');
			}
		},
		handleClickStop() {
			if (!this.isLoading) {
				this.btnClickStop();
			}
		}
	}
};
</script>
<style lang="scss" scoped>
/**
    * [Description]
    *
    * Global Button
    * Can use SCSS variables = TRUE
    *
    * All button styles and variations are included here.
    *
    * [Table of Contents]
    *
    * BASE
    * PRIMARY
    * SECONDARY
	* LINK-BUTTON
	* LINK-ALT BUTTON
    * ICON-CIRCLE-BUTTON
    * ICON-ONLY BUTTON
	* INLINE LINK BUTTON
    * SIZES
    * SPINNER BUTTON
 */

@import '@/scss/5-objects/_form.scss';

/*------------------------------------*\
  #BASE
\*------------------------------------*/

@mixin btn-clear {
	min-width: 44px; //for touch area
	font-size: inherit;
	white-space: nowrap;
	border: none;
	background-color: transparent;
	color: inherit;
	padding: 0;
}

.btn {
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
	cursor: pointer;
	outline: 0;
	border-radius: $btn-border-radius;
	border: $btn-border-width solid $base;
	background-color: $btn-default-bkgd;
	padding: $btn-vertical-padding $btn-horizontal-padding;
	@include btn-height;
	@include text-contrast($btn-default-bkgd, $white, $black);
	font-weight: $btn-font-weight;
	font-size: $btn-font-size;
	min-width: 66px; //for accessibility on mobile/tablet
	position: relative; //for loading animation position

	&:focus,
	&:hover,
	&:active {
		background-color: $base;
		@include text-contrast($base, $white, $black);
	}

	&:disabled {
		pointer-events: none;
		opacity: $disabled-opacity;
	}

	&.is-selectable {
		border-color: $neutral-3;
		background: $neutral-4;

		&:focus,
		&:active {
			background-color: $neutral-4;
			color: $black;
		}
	}

	&.is-selected {
		border-color: transparent;
		background-color: $btn-selected-bkgd;

		&:focus,
		&:active {
			background-color: $btn-selected-bkgd;
		}

		&::after {
			content: '';
			border: 2px solid $base;
			border-radius: $btn-border-radius;
			@include vertical-horizontal-center-with-transform;
			width: calc(100% + 2px);
			height: calc(100% + 2px);
			pointer-events: none;
		}
	}

	&.is-selectable:hover,
	&.is-selected:hover {
		background-color: $base;
		border-color: $base;
		@include text-contrast($base, $white, $black);
	}

	@include mq($from: lg) {
		min-width: 58px;
	}

	/*------------------------------------*\
    #PRIMARY
    \*------------------------------------*/
	&--primary {
		background-color: $btn-primary-bkgd;
		@include text-contrast($btn-primary-bkgd, $white, $black);

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-primary-bkgd-hover;
			@include text-contrast($btn-primary-bkgd-hover, $white, $black);
		}
	}

	/*------------------------------------*\
    #SECONDARY
    \*------------------------------------*/
	&--secondary {
		background-color: $btn-secondary-bkgd;
		@include text-contrast($btn-secondary-bkgd, $white, $black);
		border-color: $btn-secondary-bkgd;

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-secondary-bkgd-hover;
			@include text-contrast($btn-secondary-bkgd-hover, $white, $black);
			border-color: $btn-secondary-bkgd-hover;
		}
	}

	/*------------------------------------*\
    #LINK-BUTTON
    \*------------------------------------*/
	&--link {
		background-color: transparent;
		border-color: transparent;
		color: inherit;
		text-decoration: none;

		&:focus,
		&:hover,
		&:active {
			border: $btn-border-width solid $btn-primary-bkgd;
			border-radius: $btn-border-radius;
			background-color: $btn-primary-bkgd;
			@include text-contrast($btn-primary-bkgd, $white, $black);
			.btn__label {
				border-bottom: 0;
			}
		}

		.btn__label {
			border-bottom: 1px solid;
		}
	}

	/*------------------------------------*\
	#LINK-BUTTON ALTERNATIVE
	\*------------------------------------*/
	&--link-alt {
		background-color: transparent;
		border-color: transparent;
		color: inherit;
		text-decoration: none;

		&:focus,
		&:hover,
		&:active {
			border: $btn-border-width solid $btn-link-alt-bkgd;
			border-radius: $btn-border-radius;
			background-color: $btn-link-alt-bkgd;
			@include text-contrast($btn-link-alt-bkgd, $white, $black);
			.btn__label {
				border-bottom: 0;
			}
		}

		.btn__label {
			border-bottom: 1px solid;
		}
	}

	/*------------------------------------*\
    #HAS-ICON
    \*------------------------------------*/
	&--has-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;

		.icon-caret_down {
			font-size: 8px;
		}

		.btn-icon-before {
			margin-right: $gutter-md;
		}
		.btn-icon-after {
			margin-left: $gutter-md;
		}
	}

	/*------------------------------------*\
    #ICON-ONLY CIRCLE
    \*------------------------------------*/
	&--icon-only-circle,
	&--icon-only-circle-primary,
	&--icon-only-circle-secondary,
	&--icon-only-circle-outline {
		@include btn-clear;
		@include btn-height($font-size: 100%, $padding: $btn-icon-only-padding);
		min-width: auto; //known exception; make sure to keep enough space away from other actions for accessible touch area

		&:focus,
		&:hover,
		&:active {
			background-color: transparent;
			color: inherit;
			.icon {
				&:before {
					transform: scale(0.85);
				}
			}
		}

		.icon {
			border: 2px solid transparent;
			border-radius: 50%;
			padding: $btn-icon-only-padding;
			display: inline-flex;
			transition: transform 0.2s;

			&:before {
				width: 1em;
			}
		}
	}

	/******* Default Styles *******/

	&--icon-only-circle {
		&:focus,
		&:hover,
		&:active {
			.icon {
				border-color: $btn-secondary-bkgd;
				background-color: $btn-default-bkgd;
				@include text-contrast($btn-default-bkgd, $white, $black);
			}
		}
	}

	/******* Primary Variant Styles *******/

	&--icon-only-circle-primary {
		&:focus,
		&:hover,
		&:active {
			.icon {
				border-color: $btn-primary-bkgd-hover;
				background-color: $btn-primary-bkgd-hover;
				@include text-contrast($btn-primary-bkgd-hover, $white, $black);
			}
		}

		.icon {
			background-color: $btn-primary-bkgd;
			border-color: $btn-primary-bkgd;
			@include text-contrast($btn-primary-bkgd, $white, $black);
		}
	}

	/******* Secondary Variant Styles *******/

	&--icon-only-circle-secondary {
		&:focus,
		&:hover,
		&:active {
			.icon {
				border-color: $btn-secondary-bkgd-hover;
				background-color: $btn-secondary-bkgd-hover;
				@include text-contrast(
					$btn-secondary-bkgd-hover,
					$white,
					$black
				);
			}
		}

		.icon {
			background-color: $btn-secondary-bkgd;
			border-color: $btn-secondary-bkgd;
			@include text-contrast($btn-secondary-bkgd, $white, $black);
		}
	}

	/******* Outline Variant Styles *******/

	&--icon-only-circle-outline {
		&:focus,
		&:hover,
		&:active {
			.icon {
				box-shadow: 0px 0px 4px 3px $neutral-4;
			}
		}

		.icon {
			border-color: $base;
			background-color: $btn-default-bkgd;
			@include text-contrast($btn-default-bkgd, $white, $black);
		}
	}

	/*------------------------------------*\
    #ICON-ONLY
    \*------------------------------------*/
	&--icon-only {
		@include btn-clear;
		@include btn-height($font-size: 100%, $padding: $btn-icon-only-padding);
		min-width: auto; //known exception; make sure to keep enough space away from other actions for accessible touch area

		&:focus {
			background-color: transparent;
			color: inherit;
			.icon {
				outline: 1px solid;
			}
		}

		&:hover,
		&:active {
			background-color: transparent;
			color: inherit;
			.icon {
				font-weight: $bold-font-weight;
				text-shadow: 0px 2px 2px;
			}
		}

		.icon {
			padding: $btn-icon-only-padding;
			display: inline-flex;
		}
	}

	/*------------------------------------*\
    #INLINE LINK BUTTON
    \*------------------------------------*/
	&--inline-link {
		border-radius: 0;
		border: none;
		background-color: initial;
		padding: 0;
		@include btn-height($padding: 0px);
		min-width: initial;
		position: relative;
		font-size: inherit;
		color: $link-color;

		&::after {
			content: '';
			display: inline-block;
			width: 100%;
			@include vertical-center-with-transform;
			border-bottom: 1px solid $link-color;
			bottom: 0;
		}

		&:focus,
		&:hover,
		&:active {
			background-color: initial;
			color: $base;

			&::after {
				border: none;
			}
		}
	}

	/*------------------------------------*\
    #CAUTION
    \*------------------------------------*/

	/******* Default Variant Styles *******/
	&--caution {
		background-color: $ui-negative-light;
		border-color: $ui-negative;
		color: $black;

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-caution-dflt-bkgd;
			color: $black;
		}
	}

	/******* Primary Variant Styles *******/
	&--caution-primary {
		background-color: $ui-negative;
		border-color: $ui-negative;
		color: $white;

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-caution-bkgd-hover;
		}
	}

	/*------------------------------------*
    #SIZES
    \*------------------------------------*/
	&--small {
		padding-top: calc($btn-vertical-padding / 2);
		padding-bottom: calc($btn-vertical-padding / 2);
		@include btn-height(
			$padding: (
				calc($btn-vertical-padding / 2)
			)
		);
	}
}

/*------------------------------------*
#BUTTON SPINNER
\*------------------------------------*/

.btn-spinner-overlay {
	position: absolute;
	top: -$btn-border-width;
	bottom: -$btn-border-width;
	left: -$btn-border-width;
	right: -$btn-border-width;
	margin: auto;
	background-color: rgba($black, 0.6);
	border-radius: $btn-border-radius;
	backdrop-filter: blur(2px);
	z-index: 5;
	cursor: wait;
}

.btn--icon-only,
.btn--icon-only-circle,
.btn--icon-only-circle-primary,
.btn--icon-only-circle-secondary {
	.btn-spinner-overlay {
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		border-radius: 50%;
	}
}
</style>
